import type { ComponentPropsWithoutRef, ComponentPropsWithRef, ReactNode } from 'react';
import React, { forwardRef, useEffect, useState } from 'react';
import Image from 'next/image';

import clsx from 'clsx';

import { BASE, MD, SizesType } from '@/types/utils/sizesType';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';

//libs
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    THREADS,
    TIKTOK_BUSINESS,
    X,
    YOUTUBE
} from '@/lib/socialProfile';

//Config
import {
    avatarSize,
    backgroundSize,
    fallbackFont,
    iconGroup,
    iconInstagram,
    iconPinterest,
    iconSize,
    iconTiktok,
    iconX,
    iconYoutube,
    inSquareIcons,
    socialIcon
} from '@/components/base/Avatar/avatarConfig';
//Components
import Icon from '@/components/base/Icon';
import { Skeleton } from '@/components/base/Skeleton/Skeleton';
import Instagram from '@/components/icons/Instagram';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';

export const PROFILE_TYPE_GROUP = 'group';

type ProfileTypeType = SocialProfileNetworkType | typeof PROFILE_TYPE_GROUP;

type AvatarProps = ComponentPropsWithRef<'div'> & {
    size?: SizesType;
    radius?: string;
    selected?: boolean;
    error?: boolean;
    warning?: boolean;
    loading?: boolean;
    children: ReactNode | ReactNode[];
    forceLightMode?: boolean;
    disabled?: boolean;
    position?: 'default' | 'bottom-right';
};

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
    (
        {
            className = '',
            size = BASE,
            radius = 'rounded-full',
            selected = false,
            error = false,
            warning = false,
            loading = false,
            children,
            forceLightMode = false,
            disabled = false,
            position = 'default',
            ...restProps
        },
        ref
    ) => {
        if (loading) {
            return (
                <div
                    className="relative mr-3 inline-flex items-center justify-center"
                    data-testid="avatar-loading">
                    <Skeleton className={avatarSize[size].style} type="circle" />
                </div>
            );
        }

        return (
            <div
                ref={ref}
                className={clsx(
                    'relative inline-flex items-center justify-center bg-white',
                    className,
                    avatarSize[size]?.style,
                    radius,
                    {
                        'dark:bg-icoDarkMode-deepBlack': !forceLightMode,
                        'ring-1': selected && !disabled,
                        'ring-icoBlue': selected && !error && !warning && !disabled,
                        'ring-icoRed': selected && error && !disabled,
                        'ring-icoYellow': selected && warning && !error && !disabled
                    }
                )}
                {...restProps}>
                {React.Children.map(children, (child) => {
                    if (child) {
                        if (typeof child['type'] === 'string') {
                            return child;
                        }
                        const newChild: ReactNode = React.cloneElement(
                            child as React.ReactElement<
                                | AvatarImageContainerProps
                                | AvatarSocialProfileProps
                                | AvatarStatusProps
                            >,
                            { size, radius, error, warning, disabled, position, forceLightMode }
                        );
                        return newChild;
                    }
                    return null;
                })}
            </div>
        );
    }
);

Avatar.displayName = 'Avatar';

type AvatarImageContainerProps = Pick<
    AvatarProps,
    'radius' | 'size' | 'error' | 'forceLightMode' | 'disabled'
> & {
    customColorClass?: string;
    children: ReactNode | ReactNode[];
};

const AvatarImageContainer = ({
    customColorClass,
    radius,
    size,
    forceLightMode,
    children,
    disabled
}: AvatarImageContainerProps) => {
    return (
        <div
            data-testid="image-container"
            className={clsx(
                'group relative flex items-center justify-center overflow-hidden font-GMedium leading-none',
                {
                    'bg-icoBlue-100 text-icoBlue': customColorClass === '',
                    'dark:bg-icoBlue/20': customColorClass === '' && !forceLightMode
                },
                customColorClass,
                radius,
                avatarSize[size].wrapperStyle
            )}>
            {React.Children.map(children, (child) => {
                if (child) {
                    if (typeof child['type'] === 'string') {
                        return child;
                    }
                    const item = child as React.ReactElement<
                        AvatarImageWrapperProps | AvatarHoverButtonProps
                    >;

                    return React.cloneElement(item, {
                        size,
                        radius,
                        disabled
                    });
                }
                return null;
            })}
        </div>
    );
};

type AvatarImageWrapperProps = Pick<AvatarProps, 'size' | 'radius' | 'disabled'> & {
    children: ReactNode;
};

const AvatarImageWrapper = ({ size, radius, children, disabled }: AvatarImageWrapperProps) => (
    <div className={clsx('absolute left-0 top-0 overflow-hidden', avatarSize[size].wrapperStyle)}>
        {disabled ? (
            <div className={clsx('absolute inset-0 z-10 bg-icoGray-400 mix-blend-color', radius)} />
        ) : null}
        {React.Children.map(children, (child) => {
            const item = child as React.ReactElement<AvatarImageProps>;

            return React.cloneElement(item, {
                size,
                radius,
                disabled
            });
        })}
    </div>
);

type AvatarImageProps = Pick<AvatarProps, 'size' | 'radius' | 'disabled'> & {
    url?: string;
    fallback?: string;
    isNextImg?: boolean;
    alt?: string;
};

const AvatarImage = ({
    url,
    size,
    radius,
    fallback = 'U',
    isNextImg = false,
    alt = '',
    disabled
}: AvatarImageProps) => {
    const [hasImageError, setHasImageError] = useState(false);

    useEffect(() => {
        setHasImageError(false);
    }, [url]);

    if (!url || hasImageError) {
        return (
            <span
                className={clsx(
                    'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                    fallbackFont[size]
                )}>
                {fallback.toUpperCase()}
            </span>
        );
    }

    if (isNextImg) {
        return (
            <Image
                className={clsx(radius, { 'opacity-30': disabled })}
                src={url}
                width={avatarSize[size].width}
                height={avatarSize[size].height}
                alt={alt}
            />
        );
    }

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            className={clsx('h-full object-cover', radius, { 'opacity-30': disabled })}
            src={url}
            width={avatarSize[size].width}
            height={avatarSize[size].height}
            alt={alt}
            onError={() => setHasImageError(true)}
        />
    );
};

type AvatarHoverButtonProps = ComponentPropsWithoutRef<'button'> & {
    children: ReactNode;
    className?: string;
};

const HoverButton = ({ children, className, ...restProps }: AvatarHoverButtonProps) => {
    return (
        <button
            className={clsx(
                'absolute inset-0 z-10 hidden w-full focus:ring-0 group-hover:inline-flex group-hover:items-center group-hover:justify-center',
                className
            )}
            {...restProps}>
            {children}
        </button>
    );
};

type AvatarSocialProfileProps = Pick<AvatarProps, 'size' | 'forceLightMode' | 'disabled'> & {
    profileType?: ProfileTypeType;
};

const AvatarSocialProfile = ({
    size,
    profileType,
    forceLightMode,
    disabled
}: AvatarSocialProfileProps) => {
    if (profileType && profileType === INSTAGRAM) {
        return (
            <div
                className={clsx('rounded-md', socialIcon, backgroundSize[size], {
                    'dark:bg-icoDarkMode-deepBlack': !forceLightMode
                })}>
                <Instagram
                    color={disabled ? '#bfc6cf' : undefined}
                    forceLightMode={forceLightMode}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    size={iconInstagram[size]}
                />
            </div>
        );
    }

    if (profileType && profileType === YOUTUBE) {
        return (
            <div
                className={clsx(
                    'absolute -bottom-[2px] -right-[2px] z-10 rounded-full bg-transparent',
                    backgroundSize[size]
                )}>
                {/* Background */}
                <YoutubeIcon
                    color="white"
                    forceLightMode={forceLightMode}
                    className={clsx(
                        '[&>path]:fill-icoWhite absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                        { 'dark:[&>path]:fill-icoDarkMode-deepBlack': !forceLightMode }
                    )}
                    size={iconYoutube[size] + 4}
                />
                <div
                    className={clsx(
                        'absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 bg-white',
                        { 'dark:bg-icoDarkMode-deepBlack': !forceLightMode }
                    )}
                />
                {/* Icon */}
                <YoutubeIcon
                    color={disabled ? '#bfc6cf' : undefined}
                    forceLightMode={forceLightMode}
                    className="absolute left-1/2 top-1/2 z-1 -translate-x-1/2 -translate-y-1/2"
                    size={iconYoutube[size]}
                />
            </div>
        );
    }

    if (profileType && profileType === PINTEREST) {
        return (
            <div
                className={clsx('rounded-full', socialIcon, backgroundSize[size], {
                    'dark:bg-icoDarkMode-deepBlack': !forceLightMode
                })}>
                <PinterestIcon
                    color={disabled ? '#bfc6cf' : undefined}
                    forceLightMode={forceLightMode}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    size={iconPinterest[size]}
                />
            </div>
        );
    }

    if (profileType && profileType !== INSTAGRAM && profileType !== PROFILE_TYPE_GROUP) {
        return (
            <div
                className={clsx(socialIcon, backgroundSize[size], {
                    'dark:bg-icoDarkMode-deepBlack': !forceLightMode,
                    'rounded-sm': inSquareIcons.includes(profileType),
                    'rounded-full': !inSquareIcons.includes(profileType)
                })}>
                <Icon
                    icon={profileType}
                    className={clsx(
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                        // We need to split because of the clsx merge if iconTwitter[size] === [iconTiktok[size]], the 2nd condition replace the first one
                        {
                            [iconX[size]]: profileType === X
                        },
                        {
                            [iconTiktok[size]]: profileType === TIKTOK_BUSINESS
                        },
                        {
                            [iconSize[size]]: profileType !== X && profileType !== TIKTOK_BUSINESS
                        },
                        {
                            'text-facebook': profileType === FACEBOOK && !disabled,
                            'text-linkedin': profileType === LINKEDIN && !disabled,
                            'text-twitter': profileType === X && !disabled,
                            'text-tiktok': profileType === TIKTOK_BUSINESS && !disabled,
                            'text-threads': profileType === THREADS && !disabled,
                            'dark:text-white': !forceLightMode && !disabled,
                            '!text-icoGray-400': disabled
                        }
                    )}
                />
            </div>
        );
    }

    return null;
};

type AvatarGroupIconProps = Pick<AvatarProps, 'size' | 'forceLightMode' | 'disabled'> & {
    profileType?: ProfileTypeType;
};

const AvatarGroupIcon = ({ size, profileType, forceLightMode, disabled }: AvatarGroupIconProps) =>
    profileType === PROFILE_TYPE_GROUP ? (
        <div
            className={clsx('rounded-full', socialIcon, backgroundSize[size], {
                'dark:bg-icoDarkMode-deepBlack': !forceLightMode
            })}>
            <Icon
                icon="group"
                className={clsx(
                    'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                    iconGroup[size],
                    {
                        'text-icoBlue': !disabled,
                        'dark:text-white': !forceLightMode && !disabled,
                        '!text-icoGray-400': disabled
                    }
                )}
            />
        </div>
    ) : null;

type AvatarStatusProps = Pick<
    AvatarProps,
    'error' | 'warning' | 'forceLightMode' | 'disabled' | 'position'
>;

const AvatarStatus = ({
    error,
    warning,
    disabled,
    forceLightMode,
    position
}: AvatarStatusProps) => {
    if (error || warning) {
        return (
            <div
                role="alert"
                data-testid={`avatar-status-${error ? 'error' : 'warning'}`}
                className={clsx(
                    'absolute inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-white text-center text-8',
                    {
                        'bg-icoRed-200 text-icoRed': error,
                        'bg-icoOrange-200 text-icoOrange': warning && !error,
                        'dark:border-icoDarkMode-deepBlack': !forceLightMode,
                        'left-0 top-0': position === 'default',
                        '-bottom-1 -right-1': position === 'bottom-right'
                    }
                )}>
                <Icon
                    icon={clsx({
                        rejected: error,
                        'exclamation-point': warning
                    })}
                />
            </div>
        );
    }
    if (disabled) {
        return (
            <div
                role="alert"
                data-testid="avatar-status-disabled"
                className={clsx(
                    'absolute inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-icoGray-300 text-center text-xxs text-icoGray-400 ring-2 ring-inset ring-white dark:bg-icoDarkMode-anthracite dark:ring-icoDarkMode-deepBlack',
                    {
                        'dark:ring-icoDarkMode-deepBlack': !forceLightMode,
                        'left-0 top-0': position === 'default',
                        '-bottom-1 -right-1': position === 'bottom-right'
                    }
                )}>
                <Icon icon="rejected" className="text-8" />
            </div>
        );
    }

    return null;
};

const AvatarCompound = Object.assign({}, Avatar, {
    ImageContainer: AvatarImageContainer,
    ImageWrapper: AvatarImageWrapper,
    Image: AvatarImage,
    HoverButton,
    SocialProfile: AvatarSocialProfile,
    GroupIcon: AvatarGroupIcon,
    Status: AvatarStatus
});

export type DefaultAvatarProps = Omit<AvatarProps, 'children'> &
    Pick<AvatarImageProps, 'url' | 'fallback' | 'isNextImg' | 'alt'> &
    Pick<AvatarImageContainerProps, 'customColorClass'> &
    Pick<AvatarSocialProfileProps, 'profileType'> & { onHover?: ReactNode };

export const DefaultAvatar = ({
    className = '',
    size = MD,
    radius = 'rounded-full',
    selected = false,
    error = false,
    warning = false,
    loading = false,
    customColorClass = '',
    url = '',
    isNextImg,
    fallback = 'U',
    alt = '',
    onHover,
    profileType = null,
    ...restProps
}: DefaultAvatarProps) => (
    <AvatarCompound
        className={className}
        size={size}
        radius={radius}
        selected={selected}
        error={error}
        warning={warning}
        loading={loading}
        {...restProps}>
        <AvatarCompound.ImageContainer
            customColorClass={customColorClass}
            radius={radius}
            size={size}>
            <AvatarCompound.ImageWrapper>
                <AvatarCompound.Image
                    url={url}
                    isNextImg={isNextImg}
                    fallback={fallback}
                    alt={alt}
                />
            </AvatarCompound.ImageWrapper>
            {onHover}
        </AvatarCompound.ImageContainer>
        <AvatarCompound.SocialProfile profileType={profileType} />
        <AvatarCompound.GroupIcon profileType={profileType} />
        <AvatarCompound.Status />
    </AvatarCompound>
);

export default AvatarCompound;
